import { memo, useMemo, useState } from 'react';
import {
  BirthdayPicker,
  IconChevronDown,
  Stack,
  TextInput,
} from '@adc/parallax-component-library';
import { DateTimeFormatter, LocalDate } from '@js-joda/core';

import { buildTestId } from '~/utils/buildTestId';

// TODO: The DATE_FORMAT should be coming from department settings
//
const DATE_FORMAT = 'MM/d/yyyy';
const formatter = DateTimeFormatter.ofPattern(DATE_FORMAT);

type DatePickerProps = {
  testID: string;
  label: string;
  placeholder: string;
  value: string;
  onBlur: () => void;
  onChangeText: (v: string) => void;
  handleChange: (v: string) => void;
  error: boolean;
  supportingText: string | undefined;
};

const DatePicker = ({
  testID,
  label,
  placeholder,
  value,
  onBlur,
  onChangeText,
  handleChange,
  error,
  supportingText,
}: DatePickerProps) => {
  const [showDatePicker, setShowDatePicker] = useState(false);

  const initialDate = useMemo(() => {
    if (!value) return undefined;

    try {
      return LocalDate.parse(value, formatter);
    } catch {
      try {
        return LocalDate.parse(value, DateTimeFormatter.ISO_DATE_TIME);
      } catch {
        return LocalDate.now();
      }
    }
  }, [value]);

  return (
    <>
      <TextInput
        testID={testID}
        aria-label={label}
        label={label}
        placeholder={placeholder}
        onFocus={() => {
          setShowDatePicker(false);
        }}
        value={initialDate ? initialDate.format(formatter) : ''}
        onChangeText={onChangeText}
        onBlur={onBlur}
        autoCapitalize="none"
        autoCorrect={false}
        error={error}
        supportingText={supportingText}
        inlineIconConfig={{
          testID: buildTestId({
            testID,
            suffix: 'icon',
          }),
          onPress: () => {
            setShowDatePicker(!showDatePicker);
          },
          'aria-label': `${showDatePicker ? 'Hide' : 'Show'} the date picker`,
          icon: showDatePicker ? (
            <Stack rotateX="180deg">
              <IconChevronDown
                color="$interactive.core.enabled"
                accentColor="$interactive.core.enabled"
              />
            </Stack>
          ) : (
            <IconChevronDown
              accentColor="$interactive.core.enabled"
              color="$interactive.core.enabled"
            />
          ),
          color: '$interactive.core.enabled',
        }}
      />
      {showDatePicker && (
        <Stack
          position="absolute"
          left={0}
          top={80}
          zIndex={10}
          width="100%"
          backgroundColor="$surface.container"
          padding="$4"
          borderRadius="$2"
          shadow="$2"
        >
          <BirthdayPicker
            testID={buildTestId({
              testID,
              suffix: ['birthday', 'picker'],
            })}
            startDate={LocalDate.now().minusYears(120)}
            endDate={LocalDate.now()}
            initialDate={initialDate ?? LocalDate.now()}
            onDateChange={(date) => {
              // Avoiding re-renders
              if (date.format(formatter) !== value) {
                handleChange(date.format(formatter));
              }
            }}
          />
        </Stack>
      )}
    </>
  );
};

export default memo(DatePicker);
